/**
 * CEL completion provider for schema-based autocomplete.
 *
 * Provides intelligent autocomplete suggestions based on:
 * - CEL schema (variables, functions, constants, type fields)
 * - List macro snippets (all, exists, filter, map)
 * - Entity search (app/user lookup within specific functions)
 *
 * @module cel/completionProvider
 */

import type monaco from 'monaco-editor'
import type { AppSearchClient } from 'pbts/c1/api/app/v1/app.client'
import type { UserSearchClient } from 'pbts/c1/api/user/v1/user.client'

import { fetchEntitySuggestions, parseEntitySearchContext } from './entitySearch'
import type { SchemaRegistry } from './types'
import { isListType, matchesFilter, parseExpressionContext, resolveTypeForPath } from './utils'

/**
 * CEL macro snippets for list operations.
 * Suggested when typing after a list-type expression (e.g., `emails.`).
 */
const celMacroSnippets = [
    {
        name: 'all',
        template: 'all(${1:elem}, ${2:elem.condition})',
        doc: 'Returns true if the predicate is true for all elements in the list',
    },
    {
        name: 'exists',
        template: 'exists(${1:elem}, ${2:elem.condition})',
        doc: 'Returns true if the predicate is true for at least one element',
    },
    {
        name: 'exists_one',
        template: 'exists_one(${1:elem}, ${2:elem.condition})',
        doc: 'Returns true if the predicate is true for exactly one element',
    },
    {
        name: 'filter',
        template: 'filter(${1:elem}, ${2:elem.condition})',
        doc: 'Returns a list of elements where the predicate is true',
    },
    {
        name: 'map',
        template: 'map(${1:elem}, ${2:elem.field})',
        doc: 'Transforms each element using the given expression',
    },
]

/**
 * Create a Monaco completion provider for CEL expressions.
 *
 * Provides context-aware autocomplete:
 * - **Top-level**: Variables, functions, constants (when typing `sub|`)
 * - **Field access**: Type fields (when typing `subject.|`)
 * - **List macros**: all/exists/filter/map (when typing after a list)
 * - **Entity search**: App/user lookup in `HasApp()` and `GetByID()` calls
 *
 * @param schemaRegistry - Registry mapping model URIs to CEL schemas
 * @param appSearchClient - RPC client for app search
 * @param userSearchClient - RPC client for user search
 * @param monacoModule - Monaco editor module
 * @returns Monaco CompletionItemProvider instance
 *
 * @example
 * ```ts
 * const provider = makeCELCompletionProvider(
 *   schemaRegistry,
 *   appSearchClient,
 *   userSearchClient,
 *   monaco
 * )
 * monaco.languages.registerCompletionItemProvider('cel', provider)
 *
 * // Typing "subject." suggests: email, displayName, manager, ...
 * // Typing "emails.a" suggests: all (macro snippet)
 * // Typing "c1.user.v1.HasApp(" suggests: Sales App, HR App, ...
 * ```
 */
export const makeCELCompletionProvider = (
    schemaRegistry: SchemaRegistry,
    appSearchClient: AppSearchClient,
    userSearchClient: UserSearchClient,
    monacoModule: typeof monaco,
): monaco.languages.CompletionItemProvider => ({
    triggerCharacters: ['"', "'", '(', '.'],
    provideCompletionItems: async (model, position, _context, token): Promise<monaco.languages.CompletionList | undefined> => {
        const suggestions: monaco.languages.CompletionItem[] = []

        const line = model.getLineContent(position.lineNumber)
        const lineUpToPosition = line.slice(0, position.column)

        // 1. Entity search (app/user within function params)
        const entityContext = parseEntitySearchContext(lineUpToPosition, position.column)
        if (entityContext.searchType) {
            const entitySuggestions = await fetchEntitySuggestions(
                entityContext,
                position,
                appSearchClient,
                userSearchClient,
                monacoModule,
            )
            suggestions.push(...entitySuggestions)
        }

        // 2. Schema-based completions (variables, functions, constants, fields)
        const schema = schemaRegistry.get(model.uri.toString())
        if (schema) {
            const { basePath, partial, startColumn } = parseExpressionContext(line, position.column)
            const range = {
                startLineNumber: position.lineNumber,
                startColumn,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            }

            if (basePath.length > 0) {
                // Field completion: we have a base path like "subject" or "subject.manager"
                const targetType = resolveTypeForPath(basePath, schema)
                if (targetType) {
                    // Add macro snippets if the parent type is a list
                    if (isListType(targetType)) {
                        celMacroSnippets
                            .filter((macro) => matchesFilter(macro.name, partial))
                            .forEach((macro) => {
                                suggestions.push({
                                    label: macro.name,
                                    kind: monacoModule.languages.CompletionItemKind.Snippet,
                                    detail: 'macro',
                                    documentation: macro.doc,
                                    insertText: macro.template,
                                    insertTextRules: monacoModule.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                    range,
                                    sortText: `0${macro.name}`, // Macros first for lists
                                })
                            })
                    }

                    const typeDef = schema.types.find((t) => t.name === targetType)
                    if (typeDef) {
                        typeDef.fields
                            .filter((f) => matchesFilter(f.name, partial))
                            .forEach((field) => {
                                suggestions.push({
                                    label: field.name,
                                    kind: monacoModule.languages.CompletionItemKind.Field,
                                    detail: field.typeName,
                                    insertText: field.name,
                                    range,
                                    // Sort by match quality (after macros)
                                    sortText: field.name.toLowerCase().startsWith(partial.toLowerCase())
                                        ? `1${field.name}` : `2${field.name}`,
                                })
                            })
                    }
                }
            } else if (partial.length > 0) {
                // Top-level completions: variables, functions, constants
                // Only show when user has started typing (avoids spurious popups after '(' or ',')

                // Variables
                schema.variables
                    .filter((v) => matchesFilter(v.name, partial))
                    .forEach((v) => {
                        suggestions.push({
                            label: v.name,
                            kind: monacoModule.languages.CompletionItemKind.Variable,
                            detail: v.typeName,
                            insertText: v.name,
                            range,
                            sortText: `0${v.name}`, // Variables first
                        })
                    })

                // Functions
                schema.functions
                    .filter((fn) => matchesFilter(fn.name, partial))
                    .forEach((fn) => {
                        const signature = `(${fn.paramTypes.join(', ')}) → ${fn.returnType}`
                        suggestions.push({
                            label: fn.name,
                            kind: monacoModule.languages.CompletionItemKind.Function,
                            detail: signature,
                            insertText: `${fn.name}($0)`,
                            insertTextRules: monacoModule.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                            range,
                            sortText: `1${fn.name}`, // Functions second
                        })
                    })

                // Constants
                schema.constants
                    .filter((c) => matchesFilter(c.name, partial))
                    .forEach((c) => {
                        suggestions.push({
                            label: c.name,
                            kind: monacoModule.languages.CompletionItemKind.Constant,
                            detail: c.typeName,
                            insertText: c.name,
                            range,
                            sortText: `2${c.name}`, // Constants third
                        })
                    })
            }
        }

        // Response maybe took too long, so abort if editor has moved on
        if (token.isCancellationRequested) {
            return
        }

        if (suggestions.length === 0) {
            return
        }

        return {
            suggestions,
            incomplete: true,
        }
    },
})
